import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Material } from '../../interfaces/material';
import MaterialService from '../../services/material.service';

interface ListParams {
  take?: number;
  skip?: number;
  orderBy?: string;
  orderCriterio?: 'ASC' | 'DESC';
  descripcion?: string;
}

interface MaterialsState {
  materials: Material[];
  selectedMaterial: Material | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
}

const initialState: MaterialsState = {
  materials: [],
  selectedMaterial: null,
  loading: false,
  error: null,
  totalCount: 0
};

// Async thunks
export const fetchMaterials = createAsyncThunk(
  'materials/fetchMaterials',
  async (params?: ListParams) => {
    const [materials, totalCount] = await MaterialService.list(params);
    return { materials, totalCount };
  }
);

export const fetchMaterialById = createAsyncThunk(
  'materials/fetchMaterialById',
  async (id: number) => {
    const material = await MaterialService.getById(id);
    return material;
  }
);

const materialsSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    clearMaterials(state) {
      state.materials = [];
      state.totalCount = 0;
    },
    clearSelectedMaterial(state) {
      state.selectedMaterial = null;
    },
    clearError(state) {
      state.error = null;
    },
    setMaterials(state, action: PayloadAction<Material[]>) {
      state.materials = action.payload;
    },
    addMaterial(state, action: PayloadAction<Material>) {
      state.materials.push(action.payload);
      state.totalCount += 1;
    },
    updateMaterial(state, action: PayloadAction<Material>) {
      const index = state.materials.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.materials[index] = action.payload;
      }
      if (state.selectedMaterial?.id === action.payload.id) {
        state.selectedMaterial = action.payload;
      }
    },
    removeMaterial(state, action: PayloadAction<number>) {
      state.materials = state.materials.filter((m) => m.id !== action.payload);
      state.totalCount -= 1;
      if (state.selectedMaterial?.id === action.payload) {
        state.selectedMaterial = null;
      }
    }
  },
  extraReducers: (builder) => {
    // fetchMaterials
    builder
      .addCase(fetchMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.materials = action.payload.materials;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch materials';
      })
      // fetchMaterialById
      .addCase(fetchMaterialById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaterialById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMaterial = action.payload;
      })
      .addCase(fetchMaterialById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch material';
      });
  }
});

export const materialsActions = materialsSlice.actions;

export default materialsSlice.reducer;
